import { QueryFunctionContext, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { TOTALSALES } from '@components/totalSales/queryKey/queryKey';
import { useEffect, useMemo, useState } from 'react';
import { ITotalSalesItem, ITotalSalesResponse, PAYMETHOD } from '@dataTypes/totalSalesTypes';
import Cookies from 'js-cookie';
import { AUTH } from '@dataTypes/authTypes';
import {
  getTotalSales,
  getTotalSalesWithMaster,
  getTotalSalesWithTotalMachine,
} from '@apis/totalSales';
import axios, { AxiosError } from 'axios';
import { imgTypes } from '@dataTypes/imgTypes';
import { useSetRecoilState } from 'recoil';
import errorOrYesModalState from '../../recoil/errorOrYesModal/atom';

interface useGetTotalSalesListsProps {
  startDate: string;
  endDate: string;
  catId: string;
  method: PAYMETHOD;
  storeId: number | undefined;
}

export const useGetTotalSalesLists = ({
  startDate,
  endDate,
  catId,
  method,
  storeId,
}: useGetTotalSalesListsProps) => {
  const queryClient = useQueryClient();
  const [searchTrigger, setSearchTrigger] = useState(false);
  const [totalSalesLists, setTotalSalesLists] = useState<ITotalSalesItem[]>([]);
  const setErrorState = useSetRecoilState(errorOrYesModalState);

  useEffect(() => {
    if (searchTrigger) {
      queryClient.invalidateQueries([TOTALSALES.TOTALSALESLISTS]);
      remove();
      setTotalSalesLists([]);
    }
  }, [searchTrigger]);

  const fetchSalesData = (pageParam = 1) => {
    if (catId === '') {
      return getTotalSalesWithTotalMachine(startDate, endDate, storeId, method, pageParam);
    } else {
      return getTotalSales(startDate, endDate, catId, method, pageParam);
    }
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    isFetchingNextPage,
    remove,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<ITotalSalesResponse>(
    [TOTALSALES.TOTALSALESLISTS, startDate, endDate, catId, method, storeId],
    ({ pageParam = 1 }) => safeFetchSalesData(pageParam),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.next ? lastPage.currentPage + 1 : undefined;
      },
      enabled: !!searchTrigger,
      retry: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const newData = data.pages.flatMap((page) => page.totalSales);
        setTotalSalesLists(newData);
        setSearchTrigger(false);
      },
    },
  );

  const safeFetchSalesData = async (pageParam: number): Promise<ITotalSalesResponse> => {
    try {
      // 실제 호출
      const res = await fetchSalesData(pageParam);
      // 여기서 res 검사 (HTTP 오류를 throw하지 않도록)
      return res;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorState({
          status: true,
          imgType: imgTypes.ERROR,
          title: '데이터 조회 오류',
          text: error.response?.data?.message || error.message || '최대 3개월 선택 가능합니다.',
          modalBG: true,
        });
      }
      // 실패 시 기본 페이지 반환
      return {
        totalSales: [],
        next: false,
        currentPage: pageParam ?? 1,
      };
    }
  };

  const isTotalSalesLoading = searchTrigger && isLoading && totalSalesLists.length === 0;

  return {
    totalSalesLists,
    isTotalSalesLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    setSearchTrigger,
    hasNextPage,
  };
};
